import { Component } from '@theme/component';

/**
 * Slim product card custom element for ax-* sections.
 *
 * Only behavior here is hover-image lazy swap. Click is handled by the wrapping
 * <a> (no JS navigation). Mobile devices never fire pointerenter, so the hover
 * image fetch is skipped — free performance win.
 *
 * @extends {Component}
 */
class AxProductCard extends Component {
  #hoverLoaded = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#loadHoverImage, { once: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerenter', this.#loadHoverImage);
  }

  /**
   * Mirrors the API exposed by the native <product-card>, used by <quick-add-component>
   * (`closest('product-card, ax-product-card')`) to build the quick-add modal fetch URL.
   * @returns {HTMLAnchorElement | null}
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  /**
   * Mirrors <product-card>.getSelectedVariantId(). ax-product-card doesn't expose variant
   * radios on the card itself yet — returns null so quick-add falls back to the product URL's
   * `?variant=` already baked into the overlay link href.
   * @returns {string | null}
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  #loadHoverImage = () => {
    if (this.#hoverLoaded) return;

    const hoverImg = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-hover-src]'));
    if (!hoverImg) return;

    const src = hoverImg.getAttribute('data-hover-src');
    const srcset = hoverImg.getAttribute('data-hover-srcset');

    if (src) hoverImg.setAttribute('src', src);
    if (srcset) hoverImg.setAttribute('srcset', srcset);

    hoverImg.removeAttribute('data-hover-src');
    hoverImg.removeAttribute('data-hover-srcset');

    this.#hoverLoaded = true;
  };
}

if (!customElements.get('ax-product-card')) {
  customElements.define('ax-product-card', AxProductCard);
}
